// src/scenes/ShopScene.ts
//
// Phase 7 — between-rounds shop. Displays 2 fiches + 1 ball + 1 relic, each
// rolled by rarity weight via `rollRarityForAnte` (Phase 12: 60/25/12/3 with
// legendary filtered for ante 1). Player can buy items (deduct money, add to
// GameState), reroll (escalating $1, $2, $3 …), or skip free to next round
// (or Win on ante 3 boss). All transactions auto-save.
//
// Phase 16-03: single-stock semantics (soldDefIds) + Fibonacci standard fiche
// pricing (STANDARD_FICHE_FIBONACCI) + SOLD visual state.

import Phaser from 'phaser';
import type { Relic, Rarity } from '../state/types';
import { GameState, ficheFromDef, ballFromDef } from '../state/GameState';
import { logEvent } from '../systems/runLog';
import { FICHES, FICHES_BY_ID, ficheSpritesheet } from '../content/fiches';
import { consumableSpritesheet } from '../content/consumables';
import { ballSpritesheet } from '../content/balls';
import { BALLS, BALLS_BY_ID } from '../content/balls';
import { RELICS, RELICS_BY_ID } from '../content/relics';
import {
  FICHE_PRICES,
  BALL_PRICES,
  RELIC_PRICES,
  CONSUMABLE_PRICES,
  rollRarityForAnte,
} from '../content/prices';
import { CONSUMABLES, CONSUMABLES_BY_ID, consumableFromDef } from '../systems/consumables';
import { getRequiredFiches } from '../systems/relics';
import { saveRun } from '../systems/save';
import { pickFromArray, type Rng } from '../systems/rng';
import { addCrispText } from '../ui/textHelper';
import { ConfirmModal } from '../ui/ConfirmModal';

// Phase 16-03: Fibonacci price tiers for standard fiches within a single shop visit.
// Index capped at 4 — beyond 4 purchases the price stays at $13.
const STANDARD_FICHE_FIBONACCI = [2, 3, 5, 8, 13] as const;

type ShopItem =
  | { kind: 'fiche'; defId: string; price: number }
  | { kind: 'ball'; defId: string; price: number }
  | { kind: 'relic'; defId: string; price: number }
  | { kind: 'consumable'; defId: string; price: number };

export class ShopScene extends Phaser.Scene {
  private gs!: GameState;
  private items: ShopItem[] = [];
  private rerollCost = 1;
  private confirmModal!: ConfirmModal;
  /** Tracks defIds of non-fiche items purchased this visit (relics, balls, consumables). */
  private soldDefIds: Set<string> = new Set();
  /** Tracks slot indices of purchased non-standard fiches. Allows two same-defId fiche
   *  slots to be bought independently (soldDefIds would conflate them). */
  private soldSlotIds: Set<number> = new Set();

  constructor() {
    super({ key: 'Shop' });
  }

  init(data: { gameState: GameState; resetRoll?: boolean }): void {
    this.gs = data.gameState;
    // Reset reroll cost and sold tracking only on fresh shop entry (not on restart after buy/reroll).
    if (data.resetRoll !== false) {
      this.rerollCost = 1;
      this.soldDefIds = new Set();
      this.soldSlotIds = new Set();
      // WR-01 fix: reset Fibonacci tier on fresh shop entry so a crash-recovery
      // that restores standardFichePurchasesThisVisit > 0 from the saved RunState
      // doesn't carry a stale purchase count into the next visit. Without this reset,
      // buying a standard fiche then force-quitting would cause the next shop visit
      // to start at $3/$5/$8 instead of $2.
      this.gs.setStandardFichePurchasesThisVisit(0);
      this.rollItems();
    }
  }

  create(): void {
    this.bg();
    this.confirmModal = new ConfirmModal(this);
    this.draw();
  }

  // -------- rolling --------

  private rollItems(): void {
    const rng = this.gs.getRng();
    const fresh: ShopItem[] = [
      { kind: 'fiche', ...this.rollFiche(rng) },
      { kind: 'fiche', ...this.rollFiche(rng) },
      { kind: 'ball', ...this.rollBall(rng) },
      { kind: 'relic', ...this.rollRelic(rng) },
      { kind: 'consumable', ...this.rollConsumable(rng) }, // SHOP-11
    ];

    if (this.soldDefIds.size === 0 && this.soldSlotIds.size === 0) {
      this.items = fresh;
      return;
    }

    // Re-inject sold items at their original slot indices so SOLD persists
    // through rerolls (SHOP-09). Fiches are tracked by slot index; other items
    // by defId (relics/balls/consumables stay sold across rerolls by identity).
    this.items = fresh.map((freshItem, idx) => {
      const prev = this.items[idx];
      if (prev === undefined) return freshItem;
      if (prev.kind === 'fiche' && prev.defId !== 'standard' && this.soldSlotIds.has(idx)) {
        return prev;
      }
      if (prev.kind !== 'fiche' && this.soldDefIds.has(prev.defId)) {
        return prev;
      }
      return freshItem;
    });
  }

  private rollFiche(rng: Rng): { defId: string; price: number } {
    const ante = this.gs.getState().ante;
    // 12-REVIEW HI-01: pass the set of rarities that have at least one
    // catalogue entry so `rollRarityForAnte` never returns a tier we can't
    // satisfy. Eliminates the prior reroll-on-empty-pool loop, which drained
    // the shared run RNG non-deterministically and leaked into wheel/spin
    // outcomes downstream.
    const available = new Set<Rarity>(FICHES.map((f) => f.rarity));
    const rarity = rollRarityForAnte(rng, ante, available);
    const candidates = FICHES.filter((f) => f.rarity === rarity);
    // candidates is guaranteed non-empty: rarity came from `available`.
    const def = pickFromArray(rng, candidates);
    // Cast: legendary not in v1 price table; the `available` filter above
    // prevents legendary from being returned while no legendary fiche exists,
    // but TS can't see that invariant. Phase 14 will add legendary prices.
    const price = (FICHE_PRICES as Record<Rarity, number>)[rarity];
    return { defId: def.defId, price };
  }

  private rollBall(rng: Rng): { defId: string; price: number } {
    const ante = this.gs.getState().ante;
    const available = new Set<Rarity>(BALLS.map((b) => b.rarity));
    const rarity = rollRarityForAnte(rng, ante, available);
    const candidates = BALLS.filter((b) => b.rarity === rarity);
    const def = pickFromArray(rng, candidates);
    const price = (BALL_PRICES as Record<Rarity, number>)[rarity];
    return { defId: def.defId, price };
  }

  private rollRelic(rng: Rng): { defId: string; price: number } {
    const ante = this.gs.getState().ante;
    const ownedIds = new Set(this.gs.getState().relics.map((r) => r.defId));
    const unowned = RELICS.filter((r) => !ownedIds.has(r.defId));
    // If every relic is owned, fall back to full pool (slot will be SOLD immediately).
    const pool = unowned.length > 0 ? unowned : [...RELICS];
    const available = new Set<Rarity>(pool.map((r) => r.rarity));
    const rarity = rollRarityForAnte(rng, ante, available);
    const candidates = pool.filter((r) => r.rarity === rarity);
    const def = pickFromArray(rng, candidates);
    const price = (RELIC_PRICES as Record<Rarity, number>)[rarity];
    return { defId: def.defId, price };
  }

  private rollConsumable(rng: Rng): { defId: string; price: number } {
    const ante = this.gs.getState().ante;
    const available = new Set<Rarity>(CONSUMABLES.map((c) => c.rarity));
    const rarity = rollRarityForAnte(rng, ante, available);
    const candidates = CONSUMABLES.filter((c) => c.rarity === rarity);
    const def = pickFromArray(rng, candidates);
    const price =
      CONSUMABLE_PRICES[def.rarity as keyof typeof CONSUMABLE_PRICES] ?? CONSUMABLE_PRICES.common;
    return { defId: def.defId, price };
  }

  // -------- pricing helpers --------

  /** Returns the current standard fiche price based on purchases this visit. */
  private standardFichePrice(): number {
    const purchased = this.gs.getState().standardFichePurchasesThisVisit;
    const idx = Math.min(purchased, STANDARD_FICHE_FIBONACCI.length - 1);
    return STANDARD_FICHE_FIBONACCI[idx];
  }

  // -------- render --------

  private bg(): void {
    // Phase 17: navy background + bg-tile overlay
    this.add.rectangle(0, 0, this.scale.width, this.scale.height, 0x0d1b2a).setOrigin(0, 0);
    if (this.textures.exists('bg-tile')) {
      const tileW = 128;
      const tileH = 128;
      const cols = Math.ceil(this.scale.width / tileW) + 1;
      const rows = Math.ceil(this.scale.height / tileH) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          this.add
            .image(c * tileW, r * tileH, 'bg-tile')
            .setOrigin(0, 0)
            .setAlpha(0.08);
        }
      }
    }
    addCrispText(this, 187, 12, 'SHOP', { color: '#d4af37', fontSize: '18px' }).setOrigin(0.5, 0);
    // Phase 17: shopkeeper portrait (replaces legacy placeholder)
    if (this.textures.exists('portrait-shopkeeper')) {
      this.add.image(340, 80, 'portrait-shopkeeper').setDisplaySize(80, 107).setOrigin(0.5);
    } else if (this.textures.exists('shopkeeper')) {
      this.add.image(340, 42, 'shopkeeper').setScale(0.75).setOrigin(0.5);
    }
  }

  private draw(): void {
    // Money display
    addCrispText(this, 8, 38, `$${this.gs.getState().money}`, {
      color: '#d4af37',
      fontSize: '14px',
    });

    // Item rows
    this.items.forEach((item, idx) => {
      const y = 70 + idx * 80;
      this.drawItem(item, y);
    });

    const rerollY = 70 + this.items.length * 80 + 12;
    const money = this.gs.getState().money;
    const canReroll = money >= this.rerollCost;
    const rerollBg = canReroll ? 0x222222 : 0x1a1a1a;
    const rerollColor = canReroll ? '#ffffff' : '#777777';
    const rerollBtn = this.add.rectangle(187, rerollY, 220, 36, rerollBg);
    if (canReroll) rerollBtn.setInteractive({ useHandCursor: true });
    addCrispText(this, 187, rerollY, `Reroll  $${this.rerollCost}`, {
      color: rerollColor,
      fontSize: '13px',
    }).setOrigin(0.5);
    if (canReroll) {
      rerollBtn.on('pointerdown', () => this.tryReroll());
    }

    // Skip button — free, advances to next round or Win
    const skipY = rerollY + 50;
    const skipBtn = this.add
      .rectangle(187, skipY, 220, 36, 0x064020)
      .setInteractive({ useHandCursor: true });
    addCrispText(this, 187, skipY, 'Skip — Next Round', {
      color: '#ffffff',
      fontSize: '13px',
    }).setOrigin(0.5);
    skipBtn.on('pointerdown', () => this.advanceRoundAndExit());
  }

  private drawItem(item: ShopItem, y: number): void {
    const def = this.lookupItem(item);

    // SOLD state: non-standard items purchased this visit render greyed-out
    // with a "SOLD" overlay and no interactive handler (SHOP-07, SHOP-08, T-16-07).
    const isStandardFiche = item.kind === 'fiche' && item.defId === 'standard';
    const slotIdx = this.items.indexOf(item);
    const isSold =
      !isStandardFiche &&
      (item.kind === 'fiche' ? this.soldSlotIds.has(slotIdx) : this.soldDefIds.has(item.defId));

    if (isSold) {
      this.add.rectangle(8, y, 359, 64, 0x1a1a1a).setOrigin(0, 0).setStrokeStyle(1, 0x333333);
      const soldIcon = this.resolveIconKey(item, def.rarity);
      const iconX = soldIcon ? 56 : 16;
      if (soldIcon) {
        this.add
          .image(28, y + 32, soldIcon.key, soldIcon.frame)
          .setDisplaySize(44, 44)
          .setOrigin(0.5);
      }
      addCrispText(this, iconX, y + 8, `${def.name}  [${item.kind}/${def.rarity}]`, {
        color: '#555555',
        fontSize: '13px',
      });
      addCrispText(this, iconX, y + 28, def.description ?? '', {
        color: '#555555',
        fontSize: '11px',
        wordWrap: { width: 280 },
      });
      addCrispText(this, 351, y + 8, `$${item.price}`, {
        color: '#555555',
        fontSize: '14px',
      }).setOrigin(1, 0);
      // SOLD overlay: semi-transparent black rect + "SOLD" label
      const overlay = this.add.rectangle(8, y, 359, 64, 0x000000).setOrigin(0, 0).setAlpha(0.55);
      overlay.setDepth(10);
      addCrispText(this, 187, y + 32, 'SOLD', { color: '#888888', fontSize: '14px' })
        .setOrigin(0.5)
        .setDepth(11);
      return; // no interactive handler for sold items (T-16-07)
    }

    // --- Normal (non-sold) item rendering ---

    const state = this.gs.getState();
    // Consumables: also grey out when inventory is at cap (T-15-16)
    const inventoryFull = item.kind === 'consumable' && state.consumables.length >= 3;

    // Standard fiches: display price dynamically from Fibonacci tier (SHOP-10).
    const displayPrice = isStandardFiche ? this.standardFichePrice() : item.price;

    const affordable = state.money >= displayPrice && !inventoryFull;
    const bgColor = affordable ? 0x0a3d20 : 0x222222;
    const txtColor = affordable ? '#ffffff' : '#777777';
    const priceColor = affordable ? '#d4af37' : '#777777';

    const rect = this.add
      .rectangle(8, y, 359, 64, bgColor)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0xffffff);
    if (affordable) rect.setInteractive({ useHandCursor: true });

    const icon = this.resolveIconKey(item, def.rarity);
    const iconX = icon ? 56 : 16;
    if (icon) {
      this.add
        .image(28, y + 32, icon.key, icon.frame)
        .setDisplaySize(44, 44)
        .setOrigin(0.5);
    }
    addCrispText(this, iconX, y + 8, `${def.name}  [${item.kind}/${def.rarity}]`, {
      color: txtColor,
      fontSize: '13px',
    });
    addCrispText(this, iconX, y + 28, def.description ?? '', {
      color: affordable ? '#aaaaaa' : '#666666',
      fontSize: '11px',
      wordWrap: { width: 280 },
    });
    addCrispText(this, 351, y + 8, `$${displayPrice}`, {
      color: priceColor,
      fontSize: '14px',
    }).setOrigin(1, 0);

    if (affordable) {
      rect.on('pointerdown', () => this.buyItem(item));
    }
  }

  private lookupItem(item: ShopItem): {
    name: string;
    description?: string;
    rarity: Rarity;
  } {
    if (item.kind === 'fiche') return FICHES_BY_ID[item.defId];
    if (item.kind === 'ball') return BALLS_BY_ID[item.defId];
    if (item.kind === 'consumable') return CONSUMABLES_BY_ID[item.defId];
    return RELICS_BY_ID[item.defId];
  }

  /** Resolves the best available texture for an item icon.
   *  Returns { key, frame? } — frame is set for spritesheet sources (tetris fiches). */
  private resolveIconKey(item: ShopItem, rarity: Rarity): { key: string; frame?: number } | null {
    if (item.kind === 'fiche') {
      const sheet = ficheSpritesheet(item.defId);
      if (sheet && this.textures.exists(sheet.key)) return sheet;
      const specific = `fiche-${item.defId}`;
      if (this.textures.exists(specific)) return { key: specific };
      const k = `chip-${rarity}`;
      return this.textures.exists(k) ? { key: k } : null;
    }
    if (item.kind === 'ball') {
      const sheet = ballSpritesheet(item.defId);
      if (sheet && this.textures.exists(sheet.key)) return sheet;
      const specific = `ball-${item.defId}`;
      if (this.textures.exists(specific)) return { key: specific };
      return this.textures.exists('ball-sprite') ? { key: 'ball-sprite' } : null;
    }
    if (item.kind === 'consumable') {
      const sheet = consumableSpritesheet(item.defId);
      if (sheet && this.textures.exists(sheet.key)) return sheet;
      const k = `consumable-${item.defId}`;
      return this.textures.exists(k) ? { key: k } : null;
    }
    if (item.kind === 'relic') {
      const k = `relic-${item.defId}`;
      return this.textures.exists(k) ? { key: k } : null;
    }
    return null;
  }

  // -------- transactions --------

  private buyItem(item: ShopItem): void {
    // Re-check affordability defensively — rapid double-tap guard (T-16-07).
    const isStandardFiche = item.kind === 'fiche' && item.defId === 'standard';
    const displayPrice = isStandardFiche ? this.standardFichePrice() : item.price;
    if (this.gs.getState().money < displayPrice) return;
    // WR-04: guard over-cap at the transaction layer, not only at render time.
    if (item.kind === 'consumable' && this.gs.getState().consumables.length >= 3) return;

    // 12b (BALL-10): ball purchases route through replace-ball flow. Two
    // branches — queue-fill (no confirm; non-destructive) vs replace
    // (ConfirmModal; destructive). All money-deduct + saveRun + restart
    // happens inside the chosen branch (or onConfirm callback) so that
    // No/cancel leaves money + state untouched (T-12-08 mitigation).
    if (item.kind === 'ball') {
      const ball = ballFromDef(item.defId);
      const s = this.gs.getState();
      const hasSecondBall = s.relics.some((r) => r.defId === 'second_ball');

      // Branch 1: queue-fill — second_ball relic held + queuedBall slot empty.
      if (hasSecondBall && s.queuedBall === null) {
        this.gs.adjustMoney(-displayPrice);
        this.gs.setQueuedBall(ball);
        this.soldDefIds.add(item.defId); // single-stock: ball slots lock after buy
        saveRun(this.gs.toJSON());
        this.scene.restart({ gameState: this.gs, resetRoll: false });
        return;
      }

      // Branch 2: replace nextBall — confirmation required (destructive).
      const currentBall = s.nextBall;
      const currentName = currentBall?.name ?? 'current ball';
      this.confirmModal.open({
        title: `Replace ${currentName} with ${ball.name}?`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
        onConfirm: () => {
          this.gs.adjustMoney(-displayPrice);
          this.gs.replaceBall(ball);
          this.soldDefIds.add(item.defId); // single-stock: ball slots lock after buy
          saveRun(this.gs.toJSON());
          this.scene.restart({ gameState: this.gs, resetRoll: false });
        },
        onCancel: () => {
          // No-op: money not deducted; state unchanged.
        },
      });
      return;
    }

    // Non-ball path (fiche / relic / consumable): deduct → mutate → save → restart.
    logEvent('shop_buy', { kind: item.kind, defId: item.defId, price: displayPrice });
    this.gs.adjustMoney(-displayPrice);
    if (item.kind === 'fiche') {
      const fiche = ficheFromDef(item.defId);
      this.gs.addFicheToDeck(fiche);
      if (isStandardFiche) {
        // Fibonacci escalation: increment purchase counter for next standard fiche price.
        const current = this.gs.getState().standardFichePurchasesThisVisit;
        this.gs.setStandardFichePurchasesThisVisit(current + 1);
        // Standard fiches are NOT tracked as sold — they can be bought multiple times.
      } else {
        // Non-standard fiches: track by slot index so two same-defId fiches in
        // different slots can be purchased independently (SHOP-07 extended).
        const slotIdx = this.items.indexOf(item);
        if (slotIdx >= 0) this.soldSlotIds.add(slotIdx);
      }
    } else if (item.kind === 'consumable') {
      // T-15-15 (price check) already above; addConsumable silently rejects if full.
      const cons = consumableFromDef(item.defId);
      this.gs.addConsumable(cons);
      // Single-stock via soldDefIds (replaces old items.splice pattern — SHOP-07).
      this.soldDefIds.add(item.defId);
    } else {
      // relic
      const def = RELICS_BY_ID[item.defId];
      const relic: Relic = {
        id: 'inst-relic-' + Date.now().toString(36),
        ...def,
      };
      this.gs.addRelic(relic);
      // Single-stock: relics lock their slot after purchase (SHOP-07).
      this.soldDefIds.add(item.defId);
    }
    saveRun(this.gs.toJSON());
    // Restart preserves items/rerollCost/soldDefIds (resetRoll=false), only redraws.
    this.scene.restart({ gameState: this.gs, resetRoll: false });
  }

  private tryReroll(): void {
    if (this.gs.getState().money < this.rerollCost) return;
    this.gs.adjustMoney(-this.rerollCost);
    this.rerollCost++;
    // Clear sold state — reroll replaces all slots including previously sold ones.
    this.soldDefIds = new Set();
    this.soldSlotIds = new Set();
    this.rollItems();
    saveRun(this.gs.toJSON());
    this.scene.restart({ gameState: this.gs, resetRoll: false });
  }

  private advanceRoundAndExit(): void {
    const s = this.gs.getState();
    // Ante 3 boss cleared → Win.
    if (s.ante === 3 && s.roundIndex === 2) {
      this.scene.start('Win', { seed: s.seed });
      return;
    }
    // Reset standard fiche purchase counter on shop exit (SHOP-10).
    // This resets the Fibonacci price tier for the next shop visit.
    this.gs.setStandardFichePurchasesThisVisit(0);
    // Phase 15 (CONSUMABLE-04, T-15-18): pendingInterestBonus is applied and
    // cleared at the round boundary in RoundScene.handleRoundClear() (before Shop
    // entry) so it is already 0 by the time advanceRoundAndExit() is called.
    this.gs.advanceRound();
    // The round-clear spin does NOT call discardHand() — those fiches are still
    // in ficheHand.  Discard them now so they return to the deck before we draw
    // the opening hand for the next round.
    this.gs.discardHand();
    // Draw the next round's opening hand here (after shop) so newly purchased
    // fiches and relic effects (reliable / golden_touch) are applied before play.
    const n = getRequiredFiches(this.gs.getState().relics);
    this.gs.drawFiches(n);
    this.gs.applyHandRelics(n);
    saveRun(this.gs.toJSON());
    this.scene.start('Round', { gameState: this.gs });
  }
}
